import { operatorValueCount } from 'admin/src/components/ui/DataGrid/filters/operators';
import type { NumberRange } from 'admin/src/components/ui/DataGrid/filters/types';
import { PanelOperatorRow } from 'admin/src/components/ui/DataGrid/filters/values/PanelOperatorRow';
import type { ValuePanelProps } from 'admin/src/components/ui/DataGrid/filters/values/ValuePanelProps';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'shared/src/components/ui/new/Input';

const COMMIT_DELAY = 300;

export function NumberValue({
  column,
  operator,
  values,
  range,
  autoFocus = true,
  showOperator = true,
  onChange,
}: ValuePanelProps & { range?: NumberRange; showOperator?: boolean }) {
  const isRange = operatorValueCount(column.dataType, operator) === 2;
  const [draft, setDraft] = useState(values);

  const valuesKey = values.join('\u0000');
  const lastValuesKey = useRef(valuesKey);
  if (lastValuesKey.current !== valuesKey) {
    lastValuesKey.current = valuesKey;
    setDraft(values);
  }

  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const operatorRef = useRef(operator);
  operatorRef.current = operator;

  const commit = useMemo(() => debounce((next: string[]) => changeRef.current(operatorRef.current, next), COMMIT_DELAY), []);

  useEffect(() => () => commit.flush(), [commit]);

  function update(index: number, value: string) {
    const next = [...draft];
    next[index] = value;
    if (!isRange) next.length = 1;

    setDraft(next);
    commit(next);
  }

  return (
    <div>
      {showOperator ? (
        <PanelOperatorRow
          column={column}
          operator={operator}
          values={draft}
          fallbackSecondValue={range ? String(range.max) : undefined}
          onChange={(nextOperator, nextValues) => {
            commit.cancel();
            setDraft(nextValues);
            onChange(nextOperator, nextValues);
          }}
        />
      ) : null}
      <div className="flex items-center gap-2 p-2">
        <NumberInput
          autoFocus={autoFocus}
          aria-label={isRange ? `${column.title} minimum` : column.title}
          placeholder={isRange ? rangeHint(range, 'min') : bothHint(range)}
          value={draft[0] ?? ''}
          onChange={(event) => update(0, event.target.value)}
        />
        {isRange ? (
          <>
            <span className="text-xs text-muted-foreground">–</span>
            <NumberInput aria-label={`${column.title} maximum`} placeholder={rangeHint(range, 'max')} value={draft[1] ?? ''} onChange={(event) => update(1, event.target.value)} />
          </>
        ) : null}
      </div>
    </div>
  );
}

function NumberInput(props: React.ComponentProps<typeof Input>) {
  return (
    <Input
      {...props}
      type="number"
      className="h-7 tabular-nums tracking-tight"
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.stopPropagation();
        props.onKeyDown?.(event);
      }}
    />
  );
}

function bothHint(range?: NumberRange) {
  return range ? `${range.min} – ${range.max}` : 'Value';
}

function rangeHint(range: NumberRange | undefined, bound: 'min' | 'max') {
  if (!range) return bound === 'min' ? 'Min' : 'Max';
  return String(bound === 'min' ? range.min : range.max);
}
